import { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { BrandingConfig, DEFAULT_BRANDING, hexToHsl, formatHslForCss } from '@/types/branding'

interface BrandingContextType {
  branding: BrandingConfig
  loading: boolean
  error: Error | null
  updateBranding: (config: Partial<BrandingConfig>) => Promise<{ error: Error | null }>
  uploadLogo: (file: File) => Promise<{ url: string | null; error: Error | null }>
  uploadFavicon: (file: File) => Promise<{ url: string | null; error: Error | null }>
  refetch: () => Promise<void>
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined)

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth()
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const organizationId = profile?.organization_id

  // Fetch branding from database
  const fetchBranding = useCallback(async () => {
    if (!organizationId) {
      setBranding(DEFAULT_BRANDING)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select('name, company_name, logo_url, primary_color, accent_color, favicon_url')
        .eq('id', organizationId)
        .single()

      if (fetchError) {
        console.error('BrandingContext: organizations query failed:', fetchError.code, fetchError.message)
        setError(fetchError as unknown as Error)
        setBranding(DEFAULT_BRANDING)
        setLoading(false)
        return
      }

      if (data) {
        setBranding({
          companyName: data.company_name || data.name || DEFAULT_BRANDING.companyName,
          logoUrl: data.logo_url || DEFAULT_BRANDING.logoUrl,
          primaryColor: data.primary_color || DEFAULT_BRANDING.primaryColor,
          accentColor: data.accent_color || DEFAULT_BRANDING.accentColor,
          faviconUrl: data.favicon_url || DEFAULT_BRANDING.faviconUrl,
        })
      }
    } catch (err) {
      console.error('Error fetching branding:', err)
      setError(err as Error)
      setBranding(DEFAULT_BRANDING)
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    fetchBranding()
  }, [fetchBranding])

  // Apply CSS variables when branding changes
  useEffect(() => {
    const root = document.documentElement

    // Apply primary color
    const primaryHsl = hexToHsl(branding.primaryColor)
    if (primaryHsl) {
      root.style.setProperty('--primary', formatHslForCss(primaryHsl))
      root.style.setProperty('--ring', formatHslForCss(primaryHsl))
      
      // Create lighter variant for secondary
      const secondaryHsl = { ...primaryHsl, l: 94 }
      root.style.setProperty('--secondary', formatHslForCss(secondaryHsl))
      
      // Secondary foreground uses primary color
      root.style.setProperty('--secondary-foreground', formatHslForCss(primaryHsl))
    }

    // Apply accent color
    const accentHsl = hexToHsl(branding.accentColor)
    if (accentHsl) {
      root.style.setProperty('--accent', formatHslForCss(accentHsl))
    }

    // Update favicon if custom one is set
    if (branding.faviconUrl) {
      const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      if (favicon) {
        favicon.href = branding.faviconUrl
      }
    }

  }, [branding])

  // Update branding in database
  const updateBranding = async (config: Partial<BrandingConfig>): Promise<{ error: Error | null }> => {
    if (!organizationId) {
      return { error: new Error('No organization found') }
    }

    try {
      const updates: Record<string, unknown> = {}
      if (config.companyName !== undefined) updates.company_name = config.companyName
      if (config.logoUrl !== undefined) updates.logo_url = config.logoUrl
      if (config.primaryColor !== undefined) updates.primary_color = config.primaryColor
      if (config.accentColor !== undefined) updates.accent_color = config.accentColor
      if (config.faviconUrl !== undefined) updates.favicon_url = config.faviconUrl

      const { error: updateError } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', organizationId)

      if (updateError) {
        throw updateError
      }

      // Update local state
      setBranding(prev => ({ ...prev, ...config }))
      return { error: null }
    } catch (err) {
      console.error('Error updating branding:', err)
      return { error: err as Error }
    }
  }

  // Upload logo to storage
  const uploadLogo = async (file: File): Promise<{ url: string | null; error: Error | null }> => {
    if (!organizationId) {
      return { url: null, error: new Error('No organization found') }
    }

    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${organizationId}/logo.${fileExt}`

      // Upload file
      const { error: uploadError } = await supabase.storage
        .from('branding')
        .upload(fileName, file, { upsert: true })

      if (uploadError) {
        throw uploadError
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('branding')
        .getPublicUrl(fileName)

      const url = urlData.publicUrl

      // Update organization record
      await updateBranding({ logoUrl: url })

      return { url, error: null }
    } catch (err) {
      console.error('Error uploading logo:', err)
      return { url: null, error: err as Error }
    }
  }

  // Upload favicon to storage
  const uploadFavicon = async (file: File): Promise<{ url: string | null; error: Error | null }> => {
    if (!organizationId) {
      return { url: null, error: new Error('No organization found') }
    }

    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${organizationId}/favicon.${fileExt}`

      // Upload file
      const { error: uploadError } = await supabase.storage
        .from('branding')
        .upload(fileName, file, { upsert: true })

      if (uploadError) {
        throw uploadError
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('branding')
        .getPublicUrl(fileName)

      const url = urlData.publicUrl

      // Update organization record
      await updateBranding({ faviconUrl: url })

      return { url, error: null }
    } catch (err) {
      console.error('Error uploading favicon:', err)
      return { url: null, error: err as Error }
    }
  }

  const value = useMemo(() => ({
    branding,
    loading,
    error,
    updateBranding,
    uploadLogo,
    uploadFavicon,
    refetch: fetchBranding,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [branding, loading, error, organizationId, fetchBranding])

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  )
}

export function useBranding() {
  const context = useContext(BrandingContext)
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider')
  }
  return context
}
